import { flash, SubmitButton } from "@xola/ui-kit";
import { FormikProps } from "formik";
import merge from "lodash/merge";
import React, { useEffect, useRef, useState } from "react";
import {
    KioskGeneralPreferences,
    KioskPreferences,
    KioskPrinterPreferences,
    KioskProductPreferences,
    KioskThemePreferences,
    Permission,
    Preference,
    PreferenceContext,
    PreferenceKey,
    PreferenceList,
} from "types";
import { KioskGeneralPreferencesFields } from "./GeneralSettings";
import {
    kioskGeneralPreferenceDefaults,
    kioskPrinterPreferencesDefaults,
    kioskProductPreferenceDefaults,
    kioskThemePreferenceDefaults,
} from "./KioskPreferenceDefaults";
import { KioskPrinterPreferencesFields } from "./PrinterSettings";
import { KioskProductPreferencesFields } from "./ProductSettings";
import { KioskThemePreferencesFields } from "./ThemeSettings";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";

export const KioskSettings = () => {
    const { isGranted } = useAuthorization();
    const { id: sellerId } = useSeller();

    const [generalPrefsValues, setGeneralPrefsValues] = useState(kioskGeneralPreferenceDefaults);
    const [themePrefsValues, setThemePrefsValues] = useState(kioskThemePreferenceDefaults);
    const [productPrefsValues, setProductPrefsValues] = useState(kioskProductPreferenceDefaults);
    const [printerPrefsValues, setPrinterPrefsValues] = useState(kioskPrinterPreferencesDefaults);
    const [kioskPrefsList, setKioskPrefsList] = useState<Preference<any>[]>([]);

    useEffect(() => {
        const fetch = async () => {
            const { data } = await api.get<PreferenceList>(`/preferences?context=seller`);
            const kioskPrefs = data.data.filter((item) => item.key === PreferenceKey.XOLA_SELLER_PREFS_KIOSK);
            setKioskPrefsList(kioskPrefs);
            if (kioskPrefs.length > 0) {
                // Prefer the first (left-most) values
                const mergedValue = merge({}, ...kioskPrefs.map((pref) => pref.value));
                setGeneralPrefsValues(mergedValue.general ?? kioskGeneralPreferenceDefaults);
                setThemePrefsValues(mergedValue.theme ?? kioskThemePreferenceDefaults);
                setProductPrefsValues(mergedValue.product ?? kioskProductPreferenceDefaults);
                // Spread defaults so toggles added later get their default for sellers with older saved prefs
                setPrinterPrefsValues({ ...kioskPrinterPreferencesDefaults, ...mergedValue.printer });
            }
        };
        fetch();
    }, []);

    const kioskGeneralPreferencesFormRef = useRef<FormikProps<KioskGeneralPreferences> | null>(null);
    const kioskThemePreferencesFormRef = useRef<FormikProps<KioskThemePreferences> | null>(null);
    const kioskProductPreferencesFormRef = useRef<FormikProps<KioskProductPreferences> | null>(null);
    const kioskPrinterPreferencesFormRef = useRef<FormikProps<KioskPrinterPreferences> | null>(null);

    const handleSubmit = async () => {
        await kioskThemePreferencesFormRef.current?.submitForm();
        const themePayload = kioskThemePreferencesFormRef.current?.values ?? kioskThemePreferenceDefaults;
        await kioskProductPreferencesFormRef.current?.submitForm();
        const productPayload = kioskProductPreferencesFormRef.current?.values ?? kioskProductPreferenceDefaults;
        await kioskPrinterPreferencesFormRef.current?.submitForm();
        const printerPayload = kioskPrinterPreferencesFormRef.current?.values ?? kioskPrinterPreferencesDefaults;

        const payloadData: KioskPreferences = {
            general: kioskGeneralPreferencesFormRef.current?.values ?? kioskGeneralPreferenceDefaults,
            theme: themePayload,
            product: productPayload,
            printer: printerPayload,
        };

        const basePayload = {
            context: PreferenceContext.SELLER,
            key: PreferenceKey.XOLA_SELLER_PREFS_KIOSK,
            seller: { id: sellerId },
        };

        if (kioskPrefsList.length > 1) {
            // Merge all values, preferring the first (left-most) values
            const mergedValue = merge({}, ...kioskPrefsList.map((pref) => pref.value));

            // Overwrite with the current form values
            merge(mergedValue, payloadData);

            // Keep the first, delete the rest
            const [mostRecentPreferences, ...duplicates] = kioskPrefsList;
            for (const dup of duplicates) {
                await api.delete(`/preferences/${dup.id}`);
            }
            setKioskPrefsList([mostRecentPreferences]);
            const payload = {
                ...basePayload,
                id: mostRecentPreferences.id,
                value: mergedValue,
            };
            await api.patch(`/preferences/${mostRecentPreferences.id}?seller=${sellerId}&context=seller`, payload);
            flash.success("Successfully updated Kiosk settings");
        } else if (kioskPrefsList.length === 1) {
            const pref = kioskPrefsList[0];
            const payload = {
                ...basePayload,
                id: pref.id,
                value: payloadData,
            };
            await api.patch(`/preferences/${pref.id}?seller=${sellerId}&context=seller`, payload);
            flash.success("Successfully saved Kiosk settings");
        } else {
            const payload = {
                ...basePayload,
                value: payloadData,
            };
            await api.post("/preferences", payload);
            flash.success("Successfully saved Kiosk settings");
        }
    };

    const handleReset = () => {
        kioskGeneralPreferencesFormRef.current?.handleReset();
        kioskThemePreferencesFormRef.current?.handleReset();
        kioskProductPreferencesFormRef.current?.handleReset();
        kioskPrinterPreferencesFormRef.current?.handleReset();
    };

    return (
        <ContentLayout isWide>
            <h2 className="mt-4">Kiosk Settings</h2>
            {isGranted(Permission.PREFERENCES_KIOSK_VIEW) && (
                <div className="divide-y divide-gray-light pb-6">
                    <KioskGeneralPreferencesFields
                        ref={kioskGeneralPreferencesFormRef}
                        initialValues={generalPrefsValues}
                    />

                    <KioskThemePreferencesFields ref={kioskThemePreferencesFormRef} initialValues={themePrefsValues} />

                    <KioskProductPreferencesFields
                        ref={kioskProductPreferencesFormRef}
                        initialValues={productPrefsValues}
                    />

                    <KioskPrinterPreferencesFields
                        ref={kioskPrinterPreferencesFormRef}
                        initialValues={printerPrefsValues}
                    />
                </div>
            )}

            <ContentLayout.Footer preferenceSidebar>
                <SubmitButton type="button" onClick={async () => handleSubmit()}>
                    Save
                </SubmitButton>

                <SecondaryButton onClick={() => handleReset()}>Cancel</SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};
